import notificationAPI from '@api/notification/notification';
import { useAuthOptional } from '@AuthProvider';
import CustomNotification from '@components/notification/CustomNotification';
import * as Sentry from '@sentry/react';
import { useMutation } from '@tanstack/react-query';
import { initializeApp } from 'firebase/app';
import {
  deleteToken,
  getMessaging,
  getToken,
  isSupported,
  MessagePayload,
  Messaging,
  onMessage,
} from 'firebase/messaging';
import React, { createContext, ReactNode, useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { E_APP_ENV } from 'src/config-global';
import { useSafeWindowOpen } from 'src/hooks/window-utils';
import { firebaseConfig } from 'src/libs/notification/firebase-config';
import {
  getPromptStrategy,
  needsTokenHeal,
  readLastToken,
  readTokenMintedAt,
  recordTokenMint,
} from 'src/libs/notification/push-support';

const vapidKey = import.meta.env.VITE_FIREBASE_VAPID_KEY;
const appEnv = import.meta.env.VITE_APP_ENV;

// True when the app runs as an installed PWA. The manifest declares
// display:"fullscreen", so a "standalone"-only check is false inside the
// installed app on iOS — match the whole display-mode chain plus the legacy
// iOS `navigator.standalone` flag.
export const isStandalonePwa = () => {
  if (typeof window === 'undefined') return false;
  const standaloneByDisplayMode = ['standalone', 'fullscreen', 'minimal-ui'].some(
    (mode) => window.matchMedia(`(display-mode: ${mode})`).matches,
  );
  return standaloneByDisplayMode || (window.navigator as Navigator & { standalone?: boolean }).standalone === true;
};

const isSafari = () => {
  const ua = navigator.userAgent.toLowerCase();

  // Check if it's iOS
  const isIOS =
    ua.match('iphone') ||
    ua.match('ipad') ||
    ['iPad Simulator', 'iPhone Simulator', 'iPod Simulator', 'iPad', 'iPhone', 'iPod'].includes(navigator.platform);

  // Check if it's Safari
  const isSafariBrowser = ua.includes('safari') && !ua.includes('chrome');
  // Return true only if it's iOS Safari and NOT running as an installed PWA.
  // iOS web push is only available inside the installed PWA, so this gates push off elsewhere.
  return isIOS && isSafariBrowser && !isStandalonePwa();
};

// iOS/WebKit only surfaces the permission prompt during an active user gesture;
// calling Notification.requestPermission() outside one is a silent no-op that can
// lock the permission to 'denied'. Only prompt when there is a transient
// activation (the explicit "Enable notifications" action).
const hasUserActivation = () =>
  typeof navigator !== 'undefined' && 'userActivation' in navigator ? navigator.userActivation.isActive : true;

// Which browsers allow a prompt outside a gesture is decided by
// getPromptStrategy() (push-support.ts): Chromium keeps the login auto-prompt;
// Firefox and WebKit engines only prompt from the explicit Enable action.

interface NotificationProviderProps {
  firebaseMessaging: Messaging | undefined;
  firebaseToken: string | undefined;
  subscriberId: string | undefined;
  isNotificationsAllowed: boolean;
  requestPermission: () => Promise<string | number | undefined>;
}

const NotificationContext = createContext<NotificationProviderProps>({
  firebaseMessaging: undefined,
  firebaseToken: undefined,
  subscriberId: undefined,
  isNotificationsAllowed: false,
  requestPermission: async () => undefined,
});

const getInitialNotificationState = () => {
  try {
    return window?.Notification?.permission === 'granted' || false;
  } catch {
    return false;
  }
};

export const NotificationProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [firebaseMessaging, setFirebaseMessaging] = useState<Messaging>();
  const [firebaseToken, setFirebaseToken] = useState<string>();
  const [subscriberId, setSubscriberId] = useState<string>();
  const [isNotificationsAllowed, setIsNotificationsAllowed] = useState(getInitialNotificationState());
  const [isFirebaseInitialized, setIsFirebaseInitialized] = useState(false);
  const { i18n } = useTranslation();
  // NotificationProvider mounts at the app root for every route, including public
  // ones (e.g. /auth) that never render AuthProvider — useAuthOptional returns null
  // there instead of throwing. Every use of `user` below is already optional-chained.
  const user = useAuthOptional()?.user ?? null;
  const { openWindow } = useSafeWindowOpen();
  // The same-device token a fresh mint replaced. Sent to NC as previousToken
  // so it can drop the dead credential (a dead push subscription can still
  // pass NC's FCM dry-run validation).
  const replacedTokenRef = useRef<string | null>(null);

  const isNotificationSupported = () => {
    return typeof window !== 'undefined' && 'Notification' in window && !isSafari();
  };

  // Initialize notification permission state
  useEffect(() => {
    if (typeof window !== 'undefined' && 'Notification' in window) {
      setIsNotificationsAllowed(Notification.permission === 'granted');
    } else {
      setIsNotificationsAllowed(false);
    }
  }, []);

  const upsertSubscriberMutation = useMutation({
    mutationFn: notificationAPI.upsertSubscriber,
    onSuccess: (data) => {
      setSubscriberId(data.data.subscriberId);
    },
    onError: () => {},
  });

  const subscribeMutation = useMutation({
    mutationFn: notificationAPI.subscribe,
    onSuccess: (data) => {
      setSubscriberId(data.data.subscriberId);
    },
    onError: () => {},
  });

  // Upsert Novu subscriber on user login independent of push permission so the
  // in-app inbox bell renders even when push is unavailable (Safari Web FCM,
  // permission denied, or token retrieval failure).
  useEffect(() => {
    if (!user?.Email || subscriberId || upsertSubscriberMutation.isPending) return;
    upsertSubscriberMutation.mutate({
      email: user.Email,
      firstName: user.FirstName,
      lastName: user.LastName,
      locale: i18n.language,
    });
  }, [user?.Email, subscriberId]);

  // Initialize Firebase only once
  useEffect(() => {
    if (!isNotificationSupported() || isFirebaseInitialized) {
      if (!isNotificationSupported()) {
        setIsNotificationsAllowed(false);
      }
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        // Confirm the runtime actually supports FCM web push (covers iOS PWA edge cases)
        if (!(await isSupported())) {
          setIsNotificationsAllowed(false);
          return;
        }

        const firebaseApp = initializeApp(firebaseConfig);
        const messaging = getMessaging(firebaseApp);
        if (cancelled) return;
        setFirebaseMessaging(messaging);
        setIsFirebaseInitialized(true);

        if ('serviceWorker' in navigator) {
          await navigator.serviceWorker.register('/firebase-messaging-sw.js');
        }

        onMessage(messaging, (payload: MessagePayload) => {
          showNotification(payload);
        });
      } catch (error) {
        console.error('❌ Error initializing Firebase:', error);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isFirebaseInitialized]);

  // Self-heal the FCM token: tokens go stale after ~1 month, Android expires
  // them at 270 days, and Chrome can drop the push subscription for
  // low-engagement sites. With permission already granted this re-mints
  // silently — no prompt is ever shown.
  useEffect(() => {
    if (!firebaseMessaging || firebaseToken) return;
    if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return;

    let cancelled = false;
    (async () => {
      try {
        const registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js');
        await navigator.serviceWorker.ready;
        const subscription = await registration.pushManager.getSubscription();
        const reason = needsTokenHeal({
          permission: Notification.permission,
          mintedAt: readTokenMintedAt(),
          hasSubscription: !!subscription,
        });
        if (!reason || cancelled) return;

        try {
          await deleteToken(firebaseMessaging);
        } catch {
          // No live token to delete — expected when the subscription is dead.
        }
        const token = await getToken(firebaseMessaging, { vapidKey, serviceWorkerRegistration: registration });
        if (cancelled) return;
        replacedTokenRef.current = readLastToken();
        recordTokenMint(token);
        setFirebaseToken(token);
      } catch (error) {
        // Heal is best-effort: keep the old token and retry next app open.
        Sentry.captureException(error);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [firebaseMessaging, firebaseToken]);

  // Handle subscription when both token and email are available
  useEffect(() => {
    if (!firebaseToken) {
      return;
    }
    if (!user?.Email) {
      return;
    }
    if (!isNotificationsAllowed) {
      return;
    }

    const previousToken =
      replacedTokenRef.current && replacedTokenRef.current !== firebaseToken ? replacedTokenRef.current : undefined;
    subscribeMutation.mutate({
      firebaseToken: firebaseToken,
      previousToken,
      subscriber: {
        email: user.Email,
        firstName: user.FirstName,
        lastName: user.LastName,
        locale: i18n.language,
      },
    });
  }, [user?.Email, firebaseToken, isNotificationsAllowed]);

  // NOTE: permission IS requested automatically on login (via the post-login
  // requestPermission callers), but only on browsers that allow a prompt
  // outside a user gesture (Chromium). On iOS/WebKit and desktop Safari a
  // non-gesture call can lock the permission to 'denied', so there the prompt
  // is driven by the explicit "Enable notifications" action in
  // NotificationSettingsTab — same as the pre-existing behaviour, where those
  // platforms never showed a login prompt either.

  const showNotification = (payload: MessagePayload) => {
    // Early return for Safari iOS
    if (!isNotificationSupported()) return;

    if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
      // Title/body come from the notification block; fall back to the data
      // mirror so the toast still renders if the block ever arrives empty.
      const title = payload.notification?.title || payload.data?.title;
      const body = payload.notification?.body || payload.data?.body;
      if (title && body) {
        // Inherit the global ToastContainer defaults (top-center, 5s) so push
        // toasts look like every other toast in the app.
        toast(<CustomNotification title={title} body={body} />, {
          closeOnClick: true,
          onClick: () => payload.data?.link && openWindow({ url: payload.data.link, target: '_self' }),
        });
      }
    }
  };

  const requestPermission = async () => {
    if (!isNotificationSupported() || !firebaseMessaging) {
      setIsNotificationsAllowed(false);
      return;
    }

    try {
      if (!('serviceWorker' in navigator)) {
        throw new Error('Service Worker not supported');
      }

      // The system prompt is the only step that needs a user gesture, so it must
      // run before any awaited work below. Chromium allows prompting without a
      // gesture (the login auto-prompt path); Firefox auto-suppresses and WebKit
      // can lock the permission to 'denied' on a non-gesture call, so there we
      // skip and let the explicit Enable action call this again with an active
      // gesture.
      if (Notification.permission === 'default') {
        const strategy = getPromptStrategy();
        if (strategy === 'unsupported') {
          return;
        }
        if (!hasUserActivation() && strategy === 'gesture-required') return;
        const permission = await Notification.requestPermission();
        setIsNotificationsAllowed(permission === 'granted');
        if (permission !== 'granted') {
          if (appEnv === E_APP_ENV.DEV) toast('Notification permission denied');
          return permission;
        }
      } else {
        setIsNotificationsAllowed(Notification.permission === 'granted');
        if (Notification.permission !== 'granted') return Notification.permission;
      }

      // Register the FCM service worker explicitly and wait until it is active so
      // getToken() binds to a live push subscription instead of racing the SW.
      const registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js');
      await navigator.serviceWorker.ready;

      // Ensure the token belongs to THIS device's current push subscription: drop
      // any token cached in IndexedDB (which can be stale or carried over from
      // another device/profile) before minting a fresh one.
      try {
        await deleteToken(firebaseMessaging);
      } catch {
        // No existing token to delete — safe to ignore.
      }

      const token = await getToken(firebaseMessaging, {
        vapidKey,
        serviceWorkerRegistration: registration,
      });
      replacedTokenRef.current = readLastToken();
      recordTokenMint(token);
      setFirebaseToken(token);
      return Notification.permission;
    } catch (error) {
      console.error('Error initializing Firebase or getting FCM token:', error);
      Sentry.captureException(error);
      return;
    }
  };

  return (
    <NotificationContext.Provider
      value={{ firebaseMessaging, firebaseToken, subscriberId, isNotificationsAllowed, requestPermission }}
    >
      {children}
    </NotificationContext.Provider>
  );
};

// Custom hook for using the context
export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error('useNotification must be used within a NotificationProvider');
  }
  return context;
};
